import React, { useState, useContext, useEffect } from "react";
import {
  Button,
  withStyles,
  MobileContext,
  InputContainer,
  Input,
  TextArea,
  Toggle,
  Checkbox,
  Upload,
  DateInput
} from "components/common";
import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel
} from "react-accessible-accordion";
import moment from "moment";
import { track } from "../../../utils/analytics";
import { RadioButtons } from "components/common";
import SettingsInputContainer from "../Settings/common/SettingsInputContainer";
import { commafy } from "utils";

const exists = x => !!x || !(x ?? 1);

const acceptedFileTypes = [
  "image/*",
  "application/pdf",
  "application/msword",
  "application/rtf",
  "text/plain"
];

const commonUploadSettings = {
  fileTypes: acceptedFileTypes,
  multiple: false,
  maxFileSize: 10
};

const config = {
  recognize: {
    id: 1,
    prompt: "Do you recognize this transaction?",
    fields: [
      {
        id: "transaction_recognized",
        type: "button",
        elements: [
          { id: "yes", label: "Yes", value: "Yes" },
          { id: "no", label: "No", value: "No" }
        ]
      }
    ],
    valid: data => !!data?.fields?.transaction_recognized,
    next: data =>
      ({ Yes: "issues", No: "recognizeFollowup" }[
        data?.fields?.transaction_recognized?.value
      ])
  },
  recognizeFollowup: {
    id: 2,
    detail: () => (
      <div>
        Sometimes the store’s name doesn’t match the description on your
        statement. Try to Google the description to see if you can figure out
        who the merchant is.
      </div>
    ),
    tips: ["Use quotation marks to get an exact search!"],
    prompt: "Do you recognize the merchant now?",
    fields: [
      {
        id: "transaction_recognized_now",
        type: "button",
        elements: [
          { id: "yes", label: "Yes", value: "Yes" },
          { id: "no", label: "No", value: "No" }
        ]
      }
    ],
    valid: data => !!data?.fields?.transaction_recognized_now,
    next: data =>
      ({ Yes: "anotherIssue", No: "haveCard" }[
        data?.fields?.transaction_recognized_now?.value
      ])
  },
  haveCard: {
    id: 3,
    detail: () => <div>Hmm. This may be an unauthorized transaction.</div>,
    prompt: "Do you currently have your physical card?",
    fields: [
      {
        id: "have_card",
        type: "button",
        elements: [
          { id: "yes", label: "Yes", value: "Yes" },
          { id: "no", label: "No", value: "No" }
        ]
      }
    ],
    valid: data => !!data?.fields?.have_card,
    next: data =>
      ({ Yes: "confirmation", No: "rememberWhenLostCard" }[
        data?.fields?.have_card?.value
      ])
  },
  policeReport: {
    id: 4,
    detail: () => (
      <div>
        <p>
          If your card is lost or stolen, let’s freeze your account, so nobody
          can charge any additional unauthorized transactions. Your next step
          should be to file a police report.
        </p>
        <p>
          Filing a police report is vital to safeguarding your future identity:
          If you are the victim of further fraud or identity theft down the
          line, a police report will help serve as evidence that you were,
          indeed, the victim of a crime.
        </p>
        <p>
          Be sure to get the case number and a hard copy of the police report
          for your records. The police department may take some time to complete
          the report, so get your investigating officers business card and
          contact information so you can follow up.
        </p>
        <p>
          Once you have received a copy of your police report, please upload
          here. Continue holding on to your police report because you may need
          it if it results in issues with identity theft.
        </p>
      </div>
    ),
    prompt: "Upload your police report",
    fields: [
      {
        id: "police_report",
        type: "upload",
        elements: [
          {
            id: "policeReport",
            label: "Upload your Police Report",
            ...commonUploadSettings
          }
        ]
      },
      {
        id: "no_report",
        type: "checkbox",
        elements: [
          {
            id: "no_report",
            label:
              "I have not filed a police report yet, but I certify under perjury of law that this transaction was not authorized by me, and I am willing to file a police report",
            value: true
          }
        ]
      }
    ],
    valid: data =>
      !!data?.fields?.police_report?.value || !!data?.fields?.no_report?.value,
    next: () => "fraudReportPrompt"
  },
  confirmation: {
    id: 5,
    detail: link => (
      <div>
        We will review the information you provided. If we are not missing any
        additional information, we will get your dispute to Mastercard in a few
        days for review. Mastercard will contact the merchant for more
        information, and then deliberate on your case. It is a complicated
        process that can sometimes take over 60 days.
      </div>
    ),
    tips: ["Hit Back if you need to make changes before submitting"],
    prompt: "You are ready to submit",
    submit: true
  },
  anotherIssue: {
    id: 6,
    prompt: "Is there another issue with this transaction?",
    fields: [
      {
        id: "another_issue",
        type: "button",
        elements: [
          { id: "yes", label: "Yes", value: "Yes" },
          { id: "no", label: "No", value: "No" }
        ]
      }
    ],
    valid: data => !!data?.fields?.another_issue,
    next: data =>
      ({ Yes: "issues", No: "thankYou" }[data?.fields?.another_issue?.value])
  },
  thankYou: {
    id: 7,
    detail: () => (
      <div>
        Great, glad we were able to clear that up! It’s always a good habit to
        regularly check your statements for any errors or issues.
      </div>
    ),
    prompt: "Thank you for being a Tomo cardholder",
    exit: true
  },
  issues: {
    id: 8,
    prompt: "What sort of issues are you having with this transaction?",
    fields: [
      {
        id: "issue",
        type: "radio",
        elements: [
          {
            id: 1,
            label: "Goods or services not as described",
            value: "not_as_described"
          },
          {
            id: 2,
            label: "Incorrect amount (I was billed the wrong amount)",
            value: "incorrect_amount"
          },
          {
            id: 3,
            label:
              "Duplicate (I was billed more than once at the time of the transaction)",
            value: "duplicate"
          },
          {
            id: 4,
            label:
              "Credit not processed (I was never credited for a voided or canceled transaction)",
            value: "credit_not_processed"
          },
          {
            id: 5,
            label:
              "Canceled Recurring (I was charged despite canceling a recurring transaction)",
            value: "canceled_recurring"
          },
          { id: 6, label: "Other", value: "other" }
        ]
      }
    ],
    valid: data => !!data?.fields?.issue,
    next: data =>
      ({
        not_as_described: "goodsServices",
        incorrect_amount: "correctedAmount",
        duplicate: "duplicatesPending",
        credit_not_processed: "returnedOrVoided",
        canceled_recurring: "howCancelled",
        other: "describeIssue"
      }[data?.fields?.issue?.value])
  },
  goodsServices: {
    id: 9,
    prompt: "What were the goods or services purchased?",
    tips: [
      "Be specific! If purchased online, you can include a screenshot of the website you purchased from."
    ],
    fields: [
      {
        id: "description",
        type: "textarea",
        elements: [{ maxLength: 400 }],
        required: true
      },
      {
        id: "screenshots",
        type: "upload",
        elements: [{ ...commonUploadSettings }]
      }
    ],
    valid: data => data?.fields?.description?.value?.length >= 3,
    next: () => "goodsServicesDescription"
  },
  goodsServicesDescription: {
    id: 10,
    tips: [
      "If you can snap a quick photo of the goods or services, this will greatly increase the odds of winning your dispute"
    ],
    prompt: "How were the goods or services not as described?",
    fields: [
      {
        id: "description",
        type: "textarea",
        elements: [{ maxLength: 400 }],
        required: true
      },
      {
        id: "screenshots",
        type: "upload",
        elements: [{ ...commonUploadSettings }]
      }
    ],
    valid: data => data?.fields?.description?.value?.length >= 10,
    next: () => "merchant"
  },
  merchant: {
    id: 11,
    prompt: "Have you tried contacting the merchant?",
    fields: [
      {
        id: "merchant_contacted",
        type: "button",
        elements: [
          { id: "yes", label: "Yes", value: "Yes" },
          { id: "no", label: "No", value: "No" }
        ]
      }
    ],
    valid: data => !!data?.fields?.merchant_contacted,
    next: data =>
      ({ Yes: "merchantLastContact", No: "checkWithMerchant" }[
        data?.fields?.merchant_contacted?.value
      ])
  },
  checkWithMerchant: {
    id: 12,
    detail: () => (
      <div>
        <p>
          Before we can start your dispute, you must first attempt to resolve
          the issue with the merchant directly. Usually, the merchant is more
          than willing to resolve any issues. Mastercard also requires
          cardholders to have at least attempted to work with the merchant. You
          can call or email, though we recommend email so you can maintain a
          record of the conversation.
        </p>
      </div>
    ),
    prompt:
      "Please check back once you have attempted to work with the merchant",
    exit: true
  },
  merchantLastContact: {
    id: 13,
    detail: () => (
      <div>
        Sorry to hear you weren’t able to work things out with the merchant.
      </div>
    ),
    prompt:
      "When was the last time you heard from the merchant or attempted to contact the merchant?",
    fields: [
      {
        id: "last_contact",
        type: "date",
        elements: [{ disableFuture: true }]
      }
    ],
    valid: data => !!data?.fields?.last_contact?.value,
    next: data => (data < 3 ? "waitForMerchant" : "merchantConversation")
  },
  waitForMerchant: {
    id: 14,
    detail: () => (
      <div>
        Please give the merchant a few days to respond to your messages. Please
        check back if you haven’t heard anything in 3 days.
      </div>
    ),
    exit: true
  },
  merchantConversation: {
    id: 15,
    prompt:
      "Please describe the conversations you had or tried to have with the merchant.",
    tips: [
      "The more specific, the better!",
      "If you e-mailed the merchant, you can also upload a screenshot of the conversation."
    ],
    fields: [
      {
        id: "description",
        type: "textarea",
        elements: [{ maxLength: 400 }],
        required: true
      },
      {
        id: "screenshots",
        type: "upload",
        elements: [{ ...commonUploadSettings }]
      }
    ],
    valid: data => `${data?.fields?.description?.value || ""}`.length >= 20, // fields should be automatically validated if valid() function is defined
    next: () => "otherApplicableInformation"
  },
  otherApplicableInformation: {
    id: 16,
    prompt:
      "Any other applicable information you want us to send to Mastercard for review?",
    fields: [
      {
        id: "description",
        type: "textarea",
        elements: [{ maxLength: 400 }]
      },
      {
        id: "screenshots",
        type: "upload",
        elements: [{ ...commonUploadSettings }]
      }
    ],
    next: () => "review"
  },
  review: {
    id: 17,
    detail: data => (
      <div>
        This is the information we have for your dispute. Remember, the more
        detailed and specific you are, the better shot we have of winning this
        dispute. Please review before submitting.
      </div>
    ),
    prompt: "You are ready to submit",
    submit: true
  },
  duplicatesPending: {
    id: 18,
    prompt:
      "Seeing duplicate transactions on your account? Do one or both of the transactions show as pending?",
    fields: [
      {
        id: "duplicates_pending",
        type: "button",
        elements: [
          { id: "yes", label: "Yes", value: "Yes" },
          { id: "no", label: "No", value: "No" }
        ]
      }
    ],
    valid: data => !!data?.fields?.duplicates_pending,
    next: data =>
      ({ Yes: "waitForPendingDuplicates", No: "uploadReceipt" }[
        data?.fields?.duplicates_pending?.value
      ])
  },
  waitForPendingDuplicates: {
    id: 19,
    detail: () => (
      <div>
        Sometimes the merchant will accidentally charge the card more than once,
        but only one transaction settles. It may take a day or two for the
        transaction to fully settle.
      </div>
    ),
    prompt: "Please check back once both transactions settle on the account",
    exit: true
  },
  uploadReceipt: {
    id: 20,
    prompt: "Please upload a copy of your receipt",
    tips: [
      "Without a receipt, it will be very hard to win your dispute.",
      "If you ordered online, check your email including the spam and trash.",
      "If you ordered in person, you can visit the merchant and request they print another copy of your receipt."
    ],
    fields: [
      {
        id: "screenshots",
        type: "upload",
        elements: [{ ...commonUploadSettings }]
      },
      {
        id: "no_receipt",
        type: "checkbox",
        elements: [
          {
            id: "have_receipt",
            label:
              "I don't have a receipt. I understand this may affect my chances of winning the dispute.",
            value: true
          }
        ]
      }
    ],
    valid: data =>
      !!data?.fields?.screenshots?.value || !!data?.fields?.no_receipt?.value,
    next: () => "merchant"
  },
  returnedOrVoided: {
    id: 21,
    prompt: "Was this a returned or voided transaction?",
    fields: [
      {
        id: "returned_or_voided",
        type: "button",
        elements: [
          { id: "returned", label: "Returned", value: "Returned" },
          { id: "voided", label: "Voided", value: "Voided" }
        ]
      }
    ],
    valid: data => !!data?.fields?.returned_or_voided,
    next: data =>
      ({ Returned: "whenReturned", Voided: "uploadVoidedReceipt" }[
        data?.fields?.returned_or_voided?.value
      ])
  },
  whenReturned: {
    id: 22,
    prompt: "When did you return the merchandise?",
    fields: [
      {
        id: "returned_date",
        type: "date",
        elements: [{ disableFuture: true }]
      }
    ],
    valid: data => !!data?.fields?.returned_date?.value,
    next: () => "howReturned"
  },
  howReturned: {
    id: 23,
    prompt: "How was the merchandise returned?",
    fields: [
      {
        id: "how_returned",
        type: "button",
        elements: [
          { id: "inPerson", label: "In Person", value: "In Person" },
          { id: "mailed", label: "Mailed", value: "Mailed" }
        ]
      }
    ],
    valid: data => !!data?.fields?.how_returned,
    next: data =>
      ({ ["In Person"]: "uploadReturnReceipt", Mailed: "trackingNumber" }[
        data?.fields?.how_returned?.value
      ])
  },
  uploadReturnReceipt: {
    id: 24,
    prompt: "Please upload a copy of your return receipt",
    tips: [
      "Don’t have your return receipt? It may be very hard to win your dispute without one. In the future, make sure to hold onto your return receipt until your return credit has posted to your account."
    ],
    fields: [
      {
        id: "screenshots",
        type: "upload",
        elements: [{ ...commonUploadSettings }]
      },
      {
        id: "no_receipt",
        type: "checkbox",
        elements: [
          {
            id: "have_receipt",
            label:
              "I don't have a return receipt. I understand this may affect my chances of winning the dispute.",
            value: true
          }
        ]
      }
    ],
    valid: data =>
      !!data?.fields?.screenshots?.value || !!data?.fields?.no_receipt?.value,
    next: () => "otherApplicableInformation"
  },
  trackingNumber: {
    id: 25,
    prompt: "Do you have a tracking number for your return?",
    fields: [
      {
        id: "tracking_number",
        type: "text",
        elements: [
          {
            id: "trackingNumber",
            placeholder: "Enter your tracking number here"
          }
        ]
      },
      {
        id: "no_tracking_number",
        type: "checkbox",
        elements: [
          {
            id: "no_tracking",
            label:
              "I don't have a tracking number. I understand this may affect my chances of winning the dispute.",
            value: true
          }
        ]
      }
    ],
    valid: data =>
      data?.fields?.tracking_number?.value?.length >= 8 ||
      !!data?.fields?.no_tracking_number?.value,
    next: () => "otherApplicableInformation"
  },
  uploadVoidedReceipt: {
    id: 26,
    prompt: "Please upload a copy of your voided receipt",
    tips: [
      "Don’t have your voided receipt? It may be very hard to win your dispute without one. In the future, make sure to hold onto your voided receipt until your voided credit has posted to your account."
    ],
    fields: [
      {
        id: "screenshots",
        type: "upload",
        elements: [{ ...commonUploadSettings }]
      },
      {
        id: "no_receipt",
        type: "checkbox",
        elements: [
          {
            id: "no_receipt",
            label:
              "I don't have a voided receipt. I understand this may affect my chances of winning the dispute.",
            value: true
          }
        ]
      }
    ],
    valid: data =>
      !!data?.fields?.screenshots?.value || !!data?.fields?.no_receipt?.value,
    next: () => "otherApplicableInformation"
  },
  correctedAmount: {
    id: 27,
    prompt: "What was the amount that should have been billed?",
    fields: [
      {
        id: "corrected_amount",
        type: "number",
        elements: [{ id: "amount", type: "currency" }]
      }
    ],
    valid: data => exists(data?.fields?.corrected_amount),
    next: () => "documentation"
  },
  documentation: {
    id: 28,
    prompt: "Do you have documentation showing the correct amount?",
    fields: [
      {
        id: "documentation",
        type: "upload",
        elements: [{ ...commonUploadSettings }]
      },
      {
        id: "no_documentation",
        type: "checkbox",
        elements: [
          {
            id: "no_documentation",
            label: "I don't have documentation",
            value: true
          }
        ]
      }
    ],
    valid: data =>
      !!data?.fields?.documentation?.value ||
      !!data?.fields?.no_documentation?.value,
    next: () => "uploadReceipt"
  },
  howCancelled: {
    id: 29,
    prompt:
      "This transaction was for a recurring payment. How did you submit your request for cancellation?",
    fields: [
      {
        id: "method",
        type: "radio",
        elements: [
          {
            id: 1,
            label: "By phone",
            value: "By phone"
          },
          {
            id: 2,
            label: "By email",
            value: "By email"
          },
          {
            id: 3,
            label: "From the app",
            value: "From the app"
          }
        ]
      }
    ],
    valid: data => !!data?.fields?.method,
    next: () => "haveConfirmation"
  },
  haveConfirmation: {
    id: 30,
    prompt: "Great. Did you receive a confirmation number or email?",
    fields: [
      {
        id: "confirmation",
        type: "upload",
        elements: [{ ...commonUploadSettings }]
      },
      {
        id: "no_confirmation",
        type: "checkbox",
        elements: [
          {
            id: "no_confirmation",
            label: "I have no form of confirmation",
            value: true
          }
        ]
      }
    ],
    valid: data =>
      !!data?.fields?.confirmation?.value ||
      !!data?.fields?.no_confirmation?.value,
    next: () => "merchant"
  },
  describeIssue: {
    id: 31,
    prompt:
      "Please describe the issue with your transaction. Be as detailed as possible.",
    fields: [
      {
        id: "description",
        type: "textarea",
        elements: [{ maxLength: 400 }]
      }
    ],
    valid: data => data?.fields?.description?.value?.length >= 20,
    next: () => "caseDocumentation"
  },
  caseDocumentation: {
    id: 32,
    prompt:
      "Any relevant documentation that you think clearly supports your case?",
    fields: [
      {
        id: "documentation",
        type: "upload",
        elements: [{ ...commonUploadSettings }]
      },
      {
        id: "no_documentation",
        type: "checkbox",
        elements: [
          {
            id: "no_documentation",
            label: "I have no documentation",
            value: true
          }
        ]
      }
    ],
    valid: data =>
      !!data?.fields?.documentation?.value ||
      !!data?.fields?.no_documentation?.value,
    next: () => "merchant"
  },
  rememberWhenLostCard: {
    id: 33,
    prompt: "Do you remember when you lost possession of your card?",
    fields: [
      {
        id: "remember_when_lost",
        type: "button",
        elements: [
          { id: "yes", label: "Yes", value: "Yes" },
          { id: "no", label: "No", value: "No" }
        ]
      }
    ],
    valid: data => !!data?.fields?.remember_when_lost,
    next: data =>
      ({ Yes: "lostCardDiscoveryDate", No: "lostCardDiscoveryDescription" }[
        data?.fields?.remember_when_lost?.value
      ])
  },
  lostCardDiscoveryDate: {
    id: 34,
    prompt: "Please enter the date on which you lost possession of your card.",
    fields: [
      {
        id: "lost_date",
        type: "date",
        elements: [{ disableFuture: true, minDate: null }]
      }
    ],
    valid: data => !!data?.fields?.lost_date?.value,
    next: () => "policeReport"
  },
  lostCardDiscoveryDescription: {
    id: 35,
    prompt:
      "Please provide more details about how you lost possession of your card. Include an approximate time frame if possible.",
    fields: [
      {
        id: "lost_description",
        type: "textarea",
        elements: [{ maxLength: 400 }]
      }
    ],
    valid: data => data?.fields?.lost_description?.value?.length >= 15,
    next: () => "policeReport"
  },
  fraudReportPrompt: {
    id: 36,
    detail: () => (
      <div>
        <p>
          To allow us to go ahead with your dispute, please download and fill
          out the form below:
        </p>
        <button
          className="button bg-black hover:bg-green-primary mt-2 px-4 py-2 font-semibold text-white"
          onClick={() => {
            window.open(
              "https://tomo-public-docs.s3.us-west-1.amazonaws.com/Fraud+Claim+Form.pdf",
              "_blank"
            );
          }}
        >
          Download Form
        </button>
      </div>
    ),
    prompt:
      "Email your complete form to help@tomocredit.com within 10 business days",
    tips: [
      "If we do not receive this form within 10 business days, we may cancel your dispute"
    ],
    fields: [
      {
        id: "confirmation",
        type: "checkbox",
        elements: [
          {
            id: "understand",
            label: "I understand",
            value: true
          }
        ]
      }
    ],
    valid: data => !!data?.fields?.confirmation?.value,
    next: () => "confirmation"
  }
};

const summarize = state => {
  const { history, progress } = state;

  const getValue = (p, f, v = "value") => progress[p]?.fields?.[f]?.[v];

  // Determine which pages produce which sets of data
  // Some pages are purely for UX and do not produce data
  const dataIds = {
    recognize: ["recognized"],
    recognizeFollowup: ["recognized"],
    haveCard: ["haveCard"],
    policeReport: ["policeReport", "policeReportFiles"],
    confirmation: null,
    anotherIssue: null,
    thankYou: null,
    issues: ["issue"],
    goodsServices: ["goodsServices", "goodsServicesFiles"],
    goodsServicesDescription: [
      "goodsServicesDescription",
      "goodsServicesDescriptionFiles"
    ],
    merchant: ["merchantContacted"],
    checkWithMerchant: null,
    merchantLastContact: ["merchantContactDate"],
    waitForMerchant: null,
    merchantConversation: ["merchantConversation", "merchantConversationFiles"],
    otherApplicableInformation: ["otherInformation", "otherInformationFiles"],
    review: null,
    duplicatesPending: null,
    waitForPendingDuplicates: null,
    uploadReceipt: ["receipt", "receiptFiles"],
    returnedOrVoided: ["returnedOrVoided"],
    whenReturned: ["returnDate"],
    howReturned: ["returnMethod"],
    uploadReturnReceipt: ["returnReceipt", "returnReceiptFiles"],
    trackingNumber: ["trackingNumber"],
    uploadVoidedReceipt: ["voidedReceipt", "voidedReceiptFiles"],
    correctedAmount: ["correctedAmount"],
    documentation: ["correctedAmountFiles"],
    howCancelled: ["cancellationMethod"],
    haveConfirmation: [
      "cancellationConfirmation",
      "cancellationConfirmationFiles"
    ],
    describeIssue: ["issueDescription"],
    caseDocumentation: ["documentation", "documentationFiles"],
    rememberWhenLostCard: null,
    lostCardDiscoveryDate: ["lostOrStolenDate"],
    lostCardDiscoveryDescription: ["lostOrStolenDescription"]
  };

  // const mediaSteps = [
  //   "policeReportFiles",
  //   "goodsServicesFiles",
  //   "goodsServicesDescriptionFiles",
  //   "merchantConversationFiles",
  //   "otherInformationFiles",
  //   "receiptFiles",
  //   "returnReceiptFiles",
  //   "voidedReceiptFiles",
  //   "correctedAmountFiles",
  //   "cancellationConfirmationFiles",
  //   "documentationFiles"
  // ];

  // Transforms the page data down to discrete data points
  const transforms = {
    recognized: {
      question: "Do you recognize this transaction?",
      label: "Transaction recognized",
      transform: () =>
        getValue("recognizeFollowup", "transaction_recognized_now") ||
        getValue("recognize", "transaction_recognized")
    },
    haveCard: {
      question: "Do you currently have your physical card?",
      label: "Have card",
      transform: () => getValue("haveCard", "have_card")
    },
    policeReport: {
      question: "Have you filed a police report?",
      label: "Filed police report",
      transform: () =>
        getValue("policeReport", "no_report")
          ? "No"
          : !!getValue("policeReport", "police_report")
          ? "Yes"
          : "No"
    },
    policeReportFiles: {
      question: "Please provide a copy of your police report",
      label: "Police report files",
      transform: () => getValue("policeReport", "police_report", "description"),
      media: () => getValue("policeReport", "police_report", "value")
    },
    issue: {
      question: "What is the issue with this transaction?",
      label: "Dispute reason",
      transform: () =>
        ({
          not_as_described: "Goods/services were not as described",
          incorrect_amount: "Incorrect amount charged",
          duplicate: "Duplicate transaction",
          credit_not_processed: "Credit was not processed",
          canceled_recurring: "Recurring transaction was not canceled",
          other: "Other"
        }[getValue("issues", "issue")])
    },
    goodsServices: {
      question: "What were the goods/services purchased?",
      label: "Goods/Services",
      transform: () => getValue("goodsServices", "description")
    },
    goodsServicesFiles: {
      question: "Please provide documentation of the goods/services",
      label: "Goods/Services files",
      transform: () => getValue("goodsServices", "screenshots", "description"),
      media: () => getValue("goodsServices", "screenshots", "value")
    },
    goodsServicesDescription: {
      question: "How were the goods/services not as described?",
      label: "Problem with goods/services",
      transform: () => getValue("goodsServicesDescription", "description")
    },
    goodsServicesDescriptionFiles: {
      question:
        "Please provide documentation of how the goods/services were not as described",
      label: "Problem with goods/services files",
      transform: () =>
        getValue("goodsServicesDescription", "screenshots", "description"),
      media: () => getValue("goodsServicesDescription", "screenshots", "value")
    },
    merchantContacted: {
      question: "Have you contacted the merchant?",
      label: "Contacted merchant",
      transform: () => getValue("merchant", "merchant_contacted")
    },
    merchantContactDate: {
      question: "When did you contact the merchant?",
      label: "Contact date",
      transform: () =>
        moment(getValue("merchantLastContact", "last_contact")).format(
          "MM/DD/YYYY"
        )
    },
    merchantConversation: {
      question: "Describe your conversation with the merchant",
      label: "Merchant conversation",
      transform: () => getValue("merchantConversation", "description")
    },
    merchantConversationFiles: {
      question: "Please provide documentation of the merchant conversation",
      label: "Merchant conversation files",
      transform: () =>
        getValue("merchantConversation", "screenshots", "description"),
      media: () => getValue("merchantConversation", "screenshots", "value")
    },
    otherInformation: {
      question: "Is there any other application information?",
      label: "Other information",
      transform: () => getValue("otherApplicableInformation", "description")
    },
    otherInformationFiles: {
      question: "Please provide documentation of other application information",
      label: "Other files",
      transform: () =>
        getValue("otherApplicableInformation", "screenshots", "description"),
      media: () =>
        getValue("otherApplicableInformation", "screenshots", "value")
    },
    receipt: {
      question: "Do you have a copy of your receipt?",
      label: "Have receipt",
      transform: () =>
        getValue("uploadReceipt", "no_receipt")
          ? "No"
          : !!getValue("uploadReceipt", "screenshots")
          ? "Yes"
          : "No"
    },
    receiptFiles: {
      question: "Please provide a copy of your receipt",
      label: "Receipt files",
      transform: () => getValue("uploadReceipt", "screenshots", "description"),
      media: () => getValue("uploadReceipt", "screenshots", "value")
    },
    returnedOrVoided: {
      question: "Was this a returned or a voided transaction?",
      label: "Returned or voided",
      transform: () => getValue("returnedOrVoided", "returned_or_voided")
    },
    returnDate: {
      question: "When did you return the merchandise?",
      label: "Return date",
      transform: () =>
        moment(getValue("whenReturned", "returned_date")).format("MM/DD/YYYY")
    },
    returnMethod: {
      question: "How did you return the merchandise?",
      label: "Return method",
      transform: () => getValue("howReturned", "how_returned")
    },
    returnReceipt: {
      question: "Do you have a copy of your return receipt?",
      label: "Have return receipt",
      transform: () =>
        getValue("returnReceipt", "no_receipt")
          ? "No"
          : !!getValue("returnReceipt", "screenshots")
          ? "Yes"
          : "No"
    },
    returnReceiptFiles: {
      question: "Please provide a copy of your return receipt",
      label: "Return receipt files",
      transform: () => getValue("returnReceipt", "screenshots", "description"),
      media: () => getValue("returnReceipt", "screenshots", "value")
    },
    trackingNumber: {
      question: "Please provide the tracking number for your return",
      label: "Tracking number",
      transform: () =>
        getValue("trackingNumber", "no_tracking_number")
          ? null
          : getValue("trackingNumber", "tracking_number")
    },
    voidedReceipt: {
      question: "Do you have a copy of your voided receipt?",
      label: "Have void receipt",
      transform: () =>
        getValue("uploadVoidedReceipt", "no_receipt")
          ? "No"
          : !!getValue("uploadVoidedReceipt", "screenshots")
          ? "Yes"
          : "No"
    },
    voidedReceiptFiles: {
      question: "Please provide a copy of your voided receipt",
      label: "Voided receipt files",
      transform: () =>
        getValue("uploadVoidedReceipt", "screenshots", "description"),
      media: () => getValue("uploadVoidedReceipt", "screenshots", "value")
    },
    correctedAmount: {
      question: "What was the amount that should have been billed?",
      label: "Corrected amount",
      transform: () => getValue("correctedAmount", "corrected_amount")
    },
    correctedAmountFiles: {
      question: "Please provide documentation of the correct amount",
      label: "Corrected amount documentation",
      transform: () =>
        getValue("documentation", "documentation", "description"),
      media: () => getValue("documentation", "documentation", "value")
    },
    cancellationMethod: {
      question: "How did you submit your request for cancellation?",
      label: "Cancellation method",
      transform: () => getValue("howCancelled", "method")
    },
    cancellationConfirmation: {
      question: "Do you have confirmation that the charge was cancelled?",
      label: "Cancellation confirmation",
      transform: () =>
        getValue("haveConfirmation", "no_confirmation")
          ? "No"
          : !!getValue("haveConfirmation", "documentation")
          ? "Yes"
          : "No"
    },
    cancellationConfirmationFiles: {
      question: "Please provide documentation of the cancellation",
      label: "Cancellation confirmation files",
      transform: () =>
        getValue("haveConfirmation", "confirmation", "description"),
      media: () => getValue("haveConfirmation", "confirmation", "value")
    },
    issueDescription: {
      question: "Please describe the issue with your transaction.",
      label: "Issue description",
      transform: () => getValue("describeIssue", "description")
    },
    documentation: {
      question:
        "Do you have any relevant documentation that supports your case?",
      label: "Supporting documentation",
      transform: () =>
        getValue("caseDocumentation", "no_documentation")
          ? "No"
          : !!getValue("caseDocumentation", "documentation")
          ? "Yes"
          : "No"
    },
    documentationFiles: {
      question: "Please provide a copy of your receipt",
      label: "Supporting documentation files",
      transform: () =>
        getValue("caseDocumentation", "documentation", "description"),
      media: () => getValue("caseDocumentation", "documentation", "value")
    },
    lostOrStolenDate: {
      question: "When did you lose your card?",
      label: "Date card was lost",
      transform: () => getValue("lostCardDiscoveryDate", "lost_date")
    },
    lostOrStolenDescription: {
      question: "How did the card leave your possession?",
      label: "Circumstances around card loss",
      transform: () =>
        getValue("lostCardDiscoveryDescription", "lost_description")
    }
  };

  const dataPoints = history.reduce((a, c) => {
    const newDataIds = dataIds[c] || [];
    return [...new Set([...a, ...newDataIds])];
  }, new Set());

  const summary = dataPoints.map(dataId => ({
    label: transforms[dataId]?.label,
    question: transforms[dataId]?.question,
    value: transforms[dataId]?.transform?.(progress),
    ...(transforms[dataId]?.media &&
      transforms[dataId]?.media?.(progress) && {
        media: transforms[dataId]?.media?.(progress)
      })
  }));

  return summary;
};

const initialState = {
  currentIndex: 0,
  history: ["recognize"] // back, forward, delete history after current if new path taken
  // progress: {
  //   recognize: {
  //     fields: {
  //       transaction_recognized: { value: "yes" }
  //     },
  //     complete: true,
  //     valid: true
  //   },
  //   policeReport: {
  //     fields: {
  //       upload: { value: "https://..." }
  //     },
  //     complete: true,
  //     valid: true
  //   }
  // }
};

const Flow = ({
  transactionId,
  disputedAmount,
  timestamp,
  config,
  state,
  setState,
  setLoading,
  setSubmitted,
  setExiting,
  setError,
  submitDispute,
  isMobile,
  close
}) => {
  const { currentIndex, history } = state;
  const page = history[currentIndex];
  const {
    id,
    title,
    detail,
    prompt,
    tips,
    fields,
    valid,
    next,
    exit,
    submit
  } = config[page];

  const { progress: { [page]: pageState } = {} } = state;

  const updateProgress = ({
    page: p = page,
    state: s = state || {},
    data
  } = {}) => {
    const { progress, progress: { [p]: pageState } = {} } = s;
    setState({
      ...s,
      progress: {
        ...progress,
        [p]: {
          ...pageState,
          ...data
        }
      }
    });
  };

  const setValid = ({ page: p = page, state: s = state, valid } = {}) => {
    updateProgress({ page: p, state: s, data: { valid } });
  };

  const setComplete = ({ page: p = page, state: s = state, complete } = {}) => {
    updateProgress({ page: p, state: s, data: { complete } });
  };

  const push = ({ page: p = page, state: s = state } = {}) => {
    const { currentIndex: i, history: h } = s;
    setState({
      ...s,
      currentIndex: i + 1,
      history: [...h, p]
    });
  };

  const back = ({
    state: s = state,
    page: p = page,
    dropCurrentProgress = true
  } = {}) => {
    const { currentIndex: i, history: h } = s;
    if (i < 1) return;
    let progress = s.progress;
    if (dropCurrentProgress) {
      const { [p]: currentPageProgress, ...rest } = progress || {};
      progress = { ...rest };
    }
    setState({
      ...s,
      currentIndex: i - 1,
      history: [...h.slice(0, -1)],
      progress
    });
  };

  const showBackButton = state.currentIndex > 0;
  const showCancelButton = true;
  const showNextButton = true;

  const allowBack = state.currentIndex > 0;
  const allowCancel = true;
  const allowNext = !valid || !!valid?.(pageState);

  const confirmBack = false;
  const confirmCancel = true;
  const confirmNext = false;

  const handleBack = ({ confirm = confirmBack } = {}) => {
    back();
  };

  const handleCancel = ({ confirm = confirmCancel } = {}) => {
    setExiting(true);
  };

  const handleSubmit = ({ state: s = state } = {}) => {
    console.log("submitting with state", s);
    setLoading(true);
    submitDispute(
      {
        transactionId,
        disputedAmount,
        form: JSON.stringify({
          state: s || {},
          summary: summarize(s || {}) || {}
        })
      },
      () => {
        // go to success scenario
        console.log("submitted successfully");
        setSubmitted(true);
      },
      () => {
        // go to failure scenario
        setError("Failed to submit, please try again later.");
        setLoading(false);
      }
    );
  };

  const handleNext = ({ confirm = confirmNext } = {}) => {
    const { progress: { [page]: pageState = {} } = {} } = state;
    if (!pageState) setState({ ...state, error: "gotta fill it out" });
    else if (exit) close();
    else if (submit) handleSubmit();
    else if (!valid || valid?.(pageState)) {
      setValid({ valid: true });
      setComplete({ complete: true });
      push({ page: next(pageState) });
    } else updateProgress({ data: { error: "Invalid entry" } });
  };

  const renderButtonField = ({
    field,
    state: s = state,
    className = "flex justify-around"
  } = {}) => {
    const { progress = {}, progress: { [page]: pageState = {} } = {} } =
      s || {};
    const { fields: pageFields = {} } = pageState;

    return (
      <div key={field.id} id={field.id} className={className}>
        {field.elements.map(e => (
          <Button
            key={e.id}
            className={`rounded-full`}
            onClick={() => {
              console.log("e.value:", e.value);
              const { value } = e;
              setState({
                ...s,
                progress: {
                  ...progress,
                  [page]: {
                    ...pageState,
                    fields: {
                      ...pageFields,
                      [field.id]: { value }
                    }
                  }
                }
              });
            }}
            green={pageFields?.[field.id]?.value === e.value}
          >
            {e.label}
          </Button>
        ))}
      </div>
    );
  };

  const renderRadioField = ({
    field,
    state: s = state,
    className = ""
  } = {}) => {
    const { progress = {}, progress: { [page]: pageState = {} } = {} } =
      s || {};
    const { fields: pageFields = {} } = pageState;

    return (
      <div key={field.id} id={field.id} className={className}>
        <RadioButtons
          elements={field.elements.map(e => ({
            ...e,
            ...(e.value === pageFields?.[field.id]?.value && {
              selected: true
            })
          }))}
          component={({ element }) => (
            <div className={`${element.disabled ? "text-gray-400" : ""}`}>
              {element.label}
            </div>
          )}
          didSelect={data => {
            if (data.disabled) return;
            const { value } = data;
            setState({
              ...s,
              progress: {
                ...progress,
                [page]: {
                  ...pageState,
                  fields: {
                    ...pageFields,
                    [field.id]: { value }
                  }
                }
              }
            });
          }}
        />
      </div>
    );
  };

  const renderTextField = ({ field } = {}) => {
    const { fields: pageFields = {} } = pageState || {};
    const { elements = [] } = field;
    return (
      <div key={field.id} id={field.id} className="mb-4">
        {elements.map(el => (
          <SettingsInputContainer>
            <Input
              value={pageFields?.[field.id]?.value || ""}
              placeholder={el.placeholder || "Enter text here"}
              onChange={e => {
                const {
                  target: { value }
                } = e;
                updateProgress({
                  data: {
                    fields: {
                      ...pageFields,
                      [field.id]: { value }
                    }
                  }
                });
              }}
            />
          </SettingsInputContainer>
        ))}
      </div>
    );
  };

  const renderNumberField = ({ field } = {}) => {
    const { fields: pageFields = {} } = pageState || {};
    const { elements = [] } = field;

    return (
      <div key={field.id} id={field.id}>
        {elements.map((el, i) => {
          const { type } = el;
          if (type === "currency") {
            return (
              <SettingsInputContainer key={`inputContainer-${i}`}>
                <>
                  <span className="text-gray-500 border-r mr-2 pr-2">$</span>
                  <Input
                    key={`${history[currentIndex]}-${i}`}
                    value={pageFields?.[field.id]?.value || ""}
                    type="text"
                    placeholder="Enter a dollar amount here"
                    onChange={e => {
                      const {
                        target: { value }
                      } = e;
                      if (commafy(value))
                        updateProgress({
                          data: {
                            fields: {
                              ...pageFields,
                              [field.id]: { value: commafy(value) }
                            }
                          }
                        });
                    }}
                  />
                </>
              </SettingsInputContainer>
            );
          } else {
            return (
              <SettingsInputContainer key={`${history[currentIndex]}-${i}`}>
                <Input
                  value={pageFields?.[field.id]?.value || ""}
                  type="number"
                  placeholder="Enter a number here"
                  onChange={e => {
                    const {
                      target: { value }
                    } = e;
                    updateProgress({
                      data: {
                        fields: {
                          ...pageFields,
                          [field.id]: { value }
                        }
                      }
                    });
                  }}
                />
              </SettingsInputContainer>
            );
          }
        })}
      </div>
    );
  };

  const renderDateField = ({ field } = {}) => {
    const { fields: pageFields = {} } = pageState || {};
    const { elements: e = [{ id: "default" }] } = field;
    return (
      <div key={field.id} id={field.id}>
        {e.map((e, i) => (
          <div key={`${field.id}-${i}`} className="flex justify-center">
            <DateInput
              disableFuture={e.disableFuture}
              minDate={e.minDate === null ? undefined : moment(timestamp)}
              className="m-auto"
              onChange={value => {
                updateProgress({
                  data: {
                    fields: {
                      ...pageFields,
                      [field.id]: { value: value.format("MM/DD/YYYY") }
                    }
                  }
                });
              }}
              onError={e => {
                if (e && pageFields?.[field.id]?.value)
                  updateProgress({
                    data: {
                      fields: {
                        ...pageFields,
                        [field.id]: { value: null }
                      }
                    }
                  });
              }}
              initialValue={pageFields?.[field.id]?.value || null}
              placeholder={"MM/DD/YYYY"}
            />
          </div>
        ))}
      </div>
    );
  };

  const renderTextAreaField = ({ field, pageState } = {}) => {
    const { fields: pageFields = {} } = pageState || {};
    const { elements: e = [] } = field;
    return (
      <div key={field.id} id={field.id}>
        {e.map((e, i) => (
          <TextArea
            key={`${field.id}-${i}`}
            value={pageFields?.[field.id]?.value || ""}
            placeholder="Enter a description here"
            className="border border-gray-400 p-2"
            onChange={e => {
              const {
                target: { value }
              } = e;
              updateProgress({
                data: {
                  fields: {
                    ...pageFields,
                    [field.id]: { value }
                  }
                }
              });
            }}
            maxLength={e.maxLength}
          />
        ))}
      </div>
    );
  };

  const renderToggleField = ({ field } = {}) => {
    const { fields: pageFields = {} } = pageState || {};
    const { elements: e = [] } = field;
    return (
      <div key={field.id} id={field.id}>
        {e.map(e => (
          <>
            <Toggle
              active={!!pageFields?.[field.id]?.value}
              onChange={e => {
                const {
                  target: { checked }
                } = e;
                updateProgress({
                  data: {
                    fields: {
                      ...pageFields,
                      [field.id]: { value: !!checked }
                    }
                  }
                });
              }}
            />
            {e.label && <span className="ml-4">{e.label}</span>}
          </>
        ))}
      </div>
    );
  };

  const renderCheckboxField = ({ field } = {}) => {
    const { fields: pageFields = {} } = pageState || {};
    const { elements: e = [] } = field;
    return (
      <div key={field.id} id={field.id} className="mb-4">
        {e.map(e => (
          <Checkbox
            label={e.label}
            checked={!!pageFields?.[field.id]?.value}
            onChange={e => {
              const {
                target: { checked }
              } = e;
              updateProgress({
                data: {
                  fields: {
                    ...pageFields,
                    [field.id]: { value: !!checked || null }
                  }
                }
              });
            }}
          />
        ))}
      </div>
    );
  };

  const renderUploadField = ({ field } = {}) => {
    const { fields: pageFields = {} } = pageState || {};
    const { elements: e = [] } = field;
    const fileName = pageFields?.[field.id]?.description;
    const override = !!pageFields?.[field.id]?.value;
    return (
      <div key={field.id} id={field.id} className="mb-4">
        {e.map((e, i) => (
          <Upload
            key={`${history[currentIndex]}-${i}`}
            overrideFileName={fileName}
            overrideComplete={override}
            maxSize={(e.maxFileSize || 1) * 1048576}
            multiple={e.multiple}
            onError={data => console.log("upload error:", data)}
            onComplete={data => {
              if (override) return;
              console.log("upload complete with data:", data);
              const { key, fileName } = data;
              updateProgress({
                data: {
                  fields: {
                    ...pageFields,
                    [field.id]: { value: key, description: fileName }
                  }
                }
              });
            }}
          />
        ))}
      </div>
    );
  };

  const fieldRenderers = {
    button: renderButtonField,
    checkbox: renderCheckboxField,
    date: renderDateField,
    number: renderNumberField,
    radio: renderRadioField,
    text: renderTextField,
    textarea: renderTextAreaField,
    toggle: renderToggleField,
    upload: renderUploadField,
    unsupported: () => <div>Unsupported field type</div>
  };

  const renderFields = ({ fields, mobile, pageState } = {}) =>
    (fields || []).map(field =>
      (fieldRenderers[field?.type] || fieldRenderers.unsupported)({
        field,
        mobile,
        pageState
      })
    );

  const renderTitle = ({ title: t = title } = {}) => (
    <p className="mb-6 font-semibold text-xl text-center">
      {t || "Open a New Dispute"}
    </p>
  );

  const renderTips = ({ tips, mobile: m } = {}) =>
    tips.map(t => (
      <div
        className={`mb-4 px-${
          m ? 4 : 6
        } border rounded py-2 hover:bg-white select-none`}
      >
        <span className="fas fa-hand-point-right mr-2" />
        {t}
      </div>
    ));

  const renderPrompt = ({ prompt: p, mobile: m } = {}) => (
    <div className={`mb-4 text-center px-${m ? 4 : 6} font-bold text-lg`}>
      {p}
    </div>
  );

  const renderDetail = ({ detail: d = () => {}, mobile: m } = {}) => (
    <div className={`mb-4 text-center px-${m ? 4 : 6}`}>{d()}</div>
  );

  const renderReview = ({ state: s = state, mobile: m } = {}) => {
    const summary = summarize(s);
    return (
      <div
        className={`mb-4 ${!m ? "overflow-auto" : ""}`}
        style={{ ...(!m && { maxHeight: 500 }) }}
      >
        {summary.map(i => {
          return (
            <Accordion allowZeroExpanded={true}>
              <div className="accordion-wrap">
                <AccordionItem>
                  <AccordionItemHeading>
                    <AccordionItemButton>{i.label}</AccordionItemButton>
                  </AccordionItemHeading>
                  <AccordionItemPanel>
                    {<p>{i.value || "No information provided"}</p>}
                  </AccordionItemPanel>
                </AccordionItem>
              </div>
            </Accordion>
          );
        })}
      </div>
    );
  };

  const renderBody = ({
    detail: d = detail,
    prompt: p = prompt,
    tips: t = tips,
    fields: f = fields,
    submit: s = submit,
    isMobile: m = isMobile
  } = {}) => (
    <div>
      {d && renderDetail({ detail: d, mobile: m })}
      {s && renderReview({ mobile: m })}
      {p && renderPrompt({ prompt: p, mobile: m })}
      {t && renderTips({ tips: t, mobile: m })}
      {f && renderFields({ fields: f, mobile: m, pageState })}
    </div>
  );

  const renderPageError = () => {
    const { error } = pageState || {};
    return error && <div className="text-red-500 -mb-4 mt-2">{error}</div>;
  };

  const renderNavigationButtons = ({
    showBackButton: sb = showBackButton,
    allowBack: ab = allowBack,
    handleBack: hb = handleBack,
    showCancelButton: sc = showCancelButton,
    allowCancel: ac = allowCancel,
    handleCancel: hc = handleCancel,
    showNextButton: sn = showNextButton,
    allowNext: an = allowNext,
    handleNext: hn = handleNext,
    submit: s = submit,
    exit: e = exit
  } = {}) => (
    <div
      className={`mt-8 flex ${sb || sc ? `justify-between` : `justify-end`}`}
    >
      {[
        ["Back", sb, ab, hb],
        ["Cancel", sc, ac, hc],
        [e ? "Exit" : s ? "Submit" : "Next", sn, an, hn]
      ].map(
        ([label, show, allow, handle]) =>
          show && (
            <div key={label}>
              <Button
                disabled={!allow}
                onClick={handle}
                style={{ padding: "0.25em 1.25em" }}
                overrideStyle={label == "Cancel"}
                className={
                  label == "Cancel"
                    ? "bg-red-300 hover:bg-red-500 text-white border-red-300"
                    : // ? "text-gray-400 hover:text-white border hover:border-none hover:bg-red-500"
                      ""
                }
              >
                {label}
              </Button>
            </div>
          )
      )}
    </div>
  );

  // const Screen = () => (
  //   <div>
  //     {renderTitle()}
  //     {renderBody()}
  //     {renderPageError()}
  //     {renderNavigationButtons()}
  //   </div>
  // );

  // return <Screen />;
  return (
    <div>
      {renderTitle()}
      {renderBody()}
      {renderPageError()}
      {renderNavigationButtons()}
    </div>
  );
};

const DisputeFlowInternal = ({ data, close, submitDispute, next }) => {
  const [state, setState] = useState(initialState);
  const [started, setStarted] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exiting, setExiting] = useState(false);
  const { isMobile } = useContext(MobileContext);

  const { user, transaction } = data || {};
  const { firstName, lastName } = user || {};
  const { id: transactionId, amount, created_at: timestamp } =
    transaction || {};

  const missingData = !firstName || !lastName || !amount || !timestamp;
  const [error, setError] = useState(missingData ? "Missing data" : null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [disputedAmount, setDisputedAmount] = useState(
    amount > 0 ? amount : null
  );

  useEffect(() => {
    if (submitted) {
      setLoading(false);
      next();
    }
  }, [submitted]);

  useEffect(() => {
    setErrorMessage(
      !disputedAmount
        ? "Invalid dispute amount"
        : disputedAmount > amount
        ? "Dispute amount cannot be greater than transaction amount"
        : disputedAmount <= 0
        ? "Dispute amount must be greater than zero"
        : null
    );
  }, [disputedAmount]);

  track("DASHBOARD_TRANSACTIONS_DISPUTE_SCREEN_LOADED");

  const renderLoading = () => (
    <div>
      <div>
        <p className="mb-6 font-semibold text-xl text-center">
          Open a New Dispute
        </p>
        <div
          className="loader items-center justify-center m-auto"
          style={{ height: "80px", width: "80px" }}
        />
        <p className={`my-4 text-center px-${isMobile ? 4 : 6}`}>
          Submitting your dispute...
        </p>
      </div>
    </div>
  );

  const renderError = () => (
    <div>
      <div>
        <p className="mb-6 font-semibold text-xl text-center">
          Open a New Dispute
        </p>
        <p className={`mb-4 text-center px-${isMobile ? 4 : 6}`}>
          Something Went Wrong
        </p>
        <div className={error ? "text-red-600 my-2" : "hidden"}>{error}</div>
      </div>
      <div className="mt-4 flex justify-between">
        <button type="button" onClick={close}>
          Close
        </button>
        <Button
          onClick={() => setError(null)}
          style={{ padding: "0.25em 1.25em" }}
        >
          Go Back
        </Button>
      </div>
    </div>
  );

  const renderConfirmExit = () => (
    <div>
      <div>
        <p className="mb-6 font-semibold text-xl text-center">
          Cancel Dispute Submission
        </p>
        <div
          className={`mb-4 text-center px-${
            isMobile ? 4 : 6
          } font-bold text-lg`}
        >
          Are you sure?
        </div>
        <p className={`mb-4 text-center px-${isMobile ? 4 : 6}`}>
          If you exit now, you will lose all progress and will have to start the
          dispute submission process from the beginning
        </p>
      </div>
      <div className="mt-4 flex justify-around">
        <Button
          onClick={() => setExiting(false)}
          style={{ padding: "0.25em 1.25em" }}
        >
          Go Back
        </Button>
        <Button
          onClick={close}
          style={{ padding: "0.25em 1.25em" }}
          overrideStyle
          className={"bg-red-500 text-white border-red-300 font-bold"}
        >
          Cancel Submission
        </Button>
      </div>
    </div>
  );

  const renderInitial = ({ name, date, amount }) => {
    const handleDisputeAmountChanged = e => {
      const { value } = e.target || {};
      const number = Number(e.target.value);
      if (!isNaN(number))
        setDisputedAmount(
          value.length < number.toFixed(2).length // TODO: handle spaces and 01, try num = num.slice(0, (num.indexOf("."))+3);
            ? value ?? number
            : number.toFixed(2)
        );
    };
    return (
      <div>
        <div>
          <p className="mb-6 font-semibold text-xl text-center">
            Open a New Dispute
          </p>
          <p className={`mb-4 text-center px-${isMobile ? 4 : 6}`}>
            Please provide all of the following information so we can start
            filing your dispute with Mastercard.
          </p>
          <div className="border-b py-2">
            <p className="font-title font-semibold">Your full legal name:</p>
            <div className="ml-4">
              <input disabled type="text" value={name} />
            </div>
          </div>
          <div className="border-b py-2">
            <p className="font-title font-semibold">Date of the transaction:</p>
            <div className="ml-4">
              <input disabled type="text" disabled value={date} />
            </div>
          </div>
          <div className="border-b py-2">
            <p className="font-title font-semibold">
              Amount of the transaction:
            </p>
            <div className="ml-4">
              $
              <input disabled type="text" value={amount} />
            </div>
          </div>
          <div className="py-2">
            <p className="font-title font-semibold">Amount disputing:</p>
            <SettingsInputContainer
              className={errorMessage && "border-red-500"}
            >
              <>
                <span className="text-gray-500 border-r mr-2 pr-2">$</span>
                <Input
                  value={disputedAmount}
                  type="text"
                  placeholder="Enter a dollar amount here"
                  onChange={handleDisputeAmountChanged}
                />
              </>
            </SettingsInputContainer>
          </div>
          <div className={errorMessage ? "text-red-600 my-2" : "hidden"}>
            {errorMessage}
          </div>
        </div>
        <div className="mt-4 flex justify-between">
          <Button
            onClick={close}
            overrideStyle
            className={"bg-red-300 hover:bg-red-500 text-white border-red-300"}
            // className={"text-gray-400 hover:text-white border hover:border-none hover:bg-red-500"}
          >
            Cancel
          </Button>
          <Button
            disabled={!disputedAmount || errorMessage}
            onClick={() => setStarted(true)}
          >
            Next
          </Button>
        </div>
      </div>
    );
  };

  const renderSubmitted = () => (
    <div>
      <div>
        <p className="mb-6 font-semibold text-xl text-center">
          Open a New Dispute
        </p>
        <p className={`mb-4 text-center px-${isMobile ? 4 : 6}`}>
          Your dispute has been submitted
        </p>
      </div>
      <div className="mt-4 flex justify-between">
        <button type="button" onClick={close}>
          Done
        </button>
      </div>
    </div>
  );

  return error ? (
    renderError()
  ) : loading ? (
    renderLoading()
  ) : exiting ? (
    renderConfirmExit()
  ) : submitted ? (
    renderSubmitted()
  ) : started ? (
    // ? renderFlow({ state, setState, config })
    <Flow
      transactionId={transactionId}
      disputedAmount={disputedAmount}
      timestamp={timestamp}
      config={config}
      state={state}
      setState={setState}
      setLoading={setLoading}
      setSubmitted={setSubmitted}
      setExiting={setExiting}
      setError={setError}
      submitDispute={submitDispute}
      isMobile={isMobile}
      close={close}
    />
  ) : (
    renderInitial({
      name: firstName && lastName ? `${firstName} ${lastName}` : "unknown",
      date: timestamp ? moment(timestamp).format("MM/DD/YYYY") : "unknown",
      amount: amount ? `${amount}` : "unknown"
    })
  );
};

export default withStyles(DisputeFlowInternal);
